import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import {
	mapOrderResponseToOrder,
	withCommissionRates,
	withPreviousDisplayFields,
	withTrades
} from '../../../models/order-response.mapper';
import { MultipleCancelOrderResponse } from '../../../models/order-api.model';
import { Order, sortOrdersByListPriority } from '../../../models/order.model';
import { CommodityAccessService } from '../../../services/commodity-access.service';
import { OpenOrdersService } from '../../../services/open-orders.service';
import { OrdersService } from '../../../services/orders.service';
import { MarketDataStore } from '../../../core/market-data';
import { TradesService } from '../../../services/trades.service';

export interface OrdersStoreState {
	orders: Order[];
	loading: boolean;
	error: string | null;
}

const initialState: OrdersStoreState = {
	orders: [],
	loading: false,
	error: null
};

export const OrdersStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(({ orders, loading, error }) => ({
		isEmpty: computed(() => !loading() && !error() && orders().length === 0)
	})),
	withMethods(
		(
			store,
			openOrdersService = inject(OpenOrdersService),
			ordersService = inject(OrdersService),
			tradesService = inject(TradesService),
			marketData = inject(MarketDataStore),
			commodityAccess = inject(CommodityAccessService)
		) => {
			let requestGeneration = 0;

			async function loadOrders({
				silent = false,
				symbol
			}: { silent?: boolean; symbol?: string | null } = {}): Promise<void> {
				const generation = ++requestGeneration;
				if (!silent) {
					patchState(store, { loading: true, error: null });
				}

				try {
					const data = await firstValueFrom(openOrdersService.getOpenOrders({ symbol, pageSize: 200 }));
					if (generation !== requestGeneration) {
						return;
					}
					// `/orders` carries none of the `/trades` fields, so they are carried over
					// from the snapshot being replaced rather than blanked and refetched.
					const previousById = new Map(store.orders().map(order => [order.orderId, order]));
					// The list's own ordering, not the API's: open orders on top, executed ones last.
					const orders = sortOrdersByListPriority(
						data.items
							.map(mapOrderResponseToOrder)
							.map(order => withPreviousDisplayFields(order, previousById.get(order.orderId)))
					);
					patchState(store, { orders, ...(silent ? {} : { loading: false }) });
				} catch {
					if (generation !== requestGeneration || silent) {
						return;
					}

					// A customer without a کد بورس کالا is refused by the endpoint itself, and the
					// orders it would have listed are ones they cannot place — so that refusal is the
					// list's empty state rather than a failure to report.
					patchState(
						store,
						commodityAccess.denied()
							? { orders: [], loading: false, error: null }
							: { error: 'دریافت سفارش‌ها با خطا مواجه شد', loading: false }
					);
				}
			}

			/**
			 * Loads one order's fills. Only the detail sheet shows them, so they're fetched for
			 * the order being opened rather than for every card in the list.
			 *
			 * A failure leaves the order's existing fills alone: the sheet keeps the breakdown it
			 * already had — or simply stays without one — instead of the whole detail view erroring.
			 *
			 * The endpoint narrows on the order's symbol and date, both taken from the order
			 * already in the list — so an order the list doesn't hold has no fills to fetch.
			 */
			async function loadTrades(orderId: number): Promise<void> {
				const order = store.orders().find(candidate => candidate.orderId === orderId);
				if (!order) {
					return;
				}

				try {
					const [trades, symbol] = await Promise.all([
						firstValueFrom(tradesService.getTrades(orderId, order.symbol ?? '', order.entryDate)),
						// «ارزش معامله» is net of commission, and the rate lives on the symbol rather
						// than on the order. A failure here resolves to undefined so a commission
						// lookup can never cost the sheet the breakdown it came for.
						firstValueFrom(marketData.getSymbol(order.symbolIsin)).catch(() => undefined)
					]);
					patchState(store, {
						orders: store
							.orders()
							.map(existing =>
								existing.orderId === orderId
									? withCommissionRates(withTrades(existing, trades), symbol)
									: existing
							)
					});
				} catch {
					// The sheet keeps whatever breakdown it already had.
				}
			}

			return {
				loadOrders,
				loadTrades,

				/**
				 * Cancels an open order, then reloads — the exchange decides the order's
				 * resulting state, so it can't be patched in locally. Throws on rejection.
				 */
				async cancelOrder(orderId: number): Promise<void> {
					await firstValueFrom(ordersService.cancelOrder(orderId));
				},

				async cancelOrders(orderIds: number[]): Promise<MultipleCancelOrderResponse[]> {
					const response = await firstValueFrom(ordersService.batchCancelOrder(orderIds));
					return response;
				},
				reset() {
					patchState(store, initialState);
				}
			};
		}
	)
);
